import { useCallback, useMemo } from "react";
import { OrganizationService } from "@common/types/organizationService";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    useOrganizationServices,
    useDeleteOrganizationServiceMutation,
} from "@api/organizationService.api";
import { OrganizationServiceFormModal } from "./OrganizationServiceFormModal";
import {
    OrganizationServiceColumn,
    OrganizationServiceRow,
    organizationServicesViewConfig,
} from "./organizationServicesViewConfig";

interface Props {
    search: string;
    canManage: boolean;
    editingService: OrganizationService | null;
    onEditingServiceChange: (service: OrganizationService | null) => void;
    deletingService: OrganizationService | null;
    onDeletingServiceChange: (service: OrganizationService | null) => void;
}

interface OrganizationServiceCallbacks {
    onEditService: (serviceId: string) => void;
    onDeleteService: (serviceId: string) => void;
}

const defaultView: View = {
    type: ViewType.TASK_GROUP,
    columns: [
        {
            id: OrganizationServiceColumn.NAME,
            options: { pin: "left" },
        },
        { id: OrganizationServiceColumn.UPDATED },
        { id: OrganizationServiceColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.MD,
    },
};

export const OrganizationServicesList = ({
    search,
    canManage,
    editingService,
    onEditingServiceChange,
    deletingService,
    onDeletingServiceChange,
}: Props) => {
    const { error: errorToast } = useToaster();
    const { data: services, isFetched } = useOrganizationServices();

    const deleteServiceMutation = useDeleteOrganizationServiceMutation(
        deletingService ?? undefined,
    );

    const servicesList = useMemo(() => services ?? [], [services]);

    const servicesById = useMemo(
        () => new Map(servicesList.map((s) => [s.id, s])),
        [servicesList],
    );

    const filteredRows = useMemo<OrganizationServiceRow[]>(() => {
        const lowerSearch = search.toLowerCase();
        return servicesList
            .filter(
                (s) => !search || s.name.toLowerCase().includes(lowerSearch),
            )
            .map((s) => ({
                id: s.id,
                name: s.name,
                updatedAt: s.updatedAt,
                organizationId: s.organizationId,
            }));
    }, [servicesList, search]);

    const handleEditService = useCallback(
        (serviceId: string) => {
            const service = servicesById.get(serviceId);
            if (service) {
                onEditingServiceChange(service);
            }
        },
        [servicesById, onEditingServiceChange],
    );

    const handleDeleteService = useCallback(
        (serviceId: string) => {
            const service = servicesById.get(serviceId);
            if (service) {
                onDeletingServiceChange(service);
            }
        },
        [servicesById, onDeletingServiceChange],
    );

    const tableMeta = useMemo<
        ViewTableMeta<OrganizationServiceCallbacks, never>
    >(
        () => ({
            callbacks: canManage
                ? {
                      onEditService: handleEditService,
                      onDeleteService: handleDeleteService,
                  }
                : undefined,
        }),
        [canManage, handleEditService, handleDeleteService],
    );

    return (
        <>
            <ViewTable
                data={isFetched ? filteredRows : null}
                view={defaultView}
                config={organizationServicesViewConfig}
                emptyView={() => <EmptyState header="No services found" />}
                loadingView={() => <Loader />}
                tableMeta={tableMeta}
                enableColumnResizing={false}
                outerBorder
            />

            <OrganizationServiceFormModal
                show={!!editingService}
                service={editingService ?? undefined}
                onHide={() => onEditingServiceChange(null)}
            />

            <ConfirmationModal
                show={!!deletingService}
                title={`Delete ${deletingService?.name}?`}
                question="This service will be permanently removed."
                yes="Delete"
                no="Cancel"
                onReject={() => onDeletingServiceChange(null)}
                onConfirm={async () => {
                    try {
                        await deleteServiceMutation.mutateAsync();
                        onDeletingServiceChange(null);
                    } catch {
                        errorToast("Failed to delete service");
                    }
                }}
            />
        </>
    );
};
